// Настройки обработки: чтение из формы, подписи, блокировка на время работы.
//
// Отдельно от main.ts, потому что тут два десятка ручек и своя дисциплина:
// сервер применяет почти всё на хендшейке, а меняется на лету только тон,
// индекс и защита согласных. Поэтому во время сессии эти контролы блокируются
// — иначе крутишь ползунок, ничего не происходит, и виноват как будто сервер.

import type { SessionConfig } from "./net/protocol.ts";
import type { SessionLimits } from "./net/http.ts";
import * as storage from "./storage.ts";
import type { ThresholdSettings } from "./ui/level.ts";

const STORAGE_KEY = "rustvoice.settings";

/**
 * Версия набора умолчаний.
 *
 * Сохранённые настройки перекрывают умолчания из разметки, поэтому смена
 * умолчаний сама по себе ни на что не влияет: у того, кто уже открывал
 * страницу, всё останется как было. Версию поднимают, когда новые умолчания
 * должны победить сохранённое — тогда старое отбрасывается.
 */
const DEFAULTS_VERSION = 5;

/**
 * Умолчания порога по режимам — приезжают с сервера, здесь только запасные.
 *
 * Запасные нужны на случай старого сервера, который этого поля ещё не отдаёт:
 * без них при смене режима подставился бы ноль, то есть детектор, пропускающий
 * всё подряд.
 */
let vadDefaults: Record<string, number> = { silero: 0.5, energy: 0.02 };

/** Режим, при котором порог настраивали в последний раз. */
let lastVadMode: string | null = null;

/** Один контрол: элемент, подпись и то, как её рисовать. */
interface Knob {
  input: HTMLInputElement | HTMLSelectElement;
  output: HTMLOutputElement | null;
  render?: (value: number) => string;
}

const num = (id: string) => document.getElementById(id) as HTMLInputElement;
const out = (id: string) => document.getElementById(id) as HTMLOutputElement | null;

const ms = (v: number) => `${v} мс`;

export const knobs: Record<string, Knob> = {
  inputGain: {
    input: num("input-gain"),
    output: out("input-gain-value"),
    render: (v) => (v === 0 ? "как есть" : `${v > 0 ? "+" : ""}${v} дБ`),
  },
  highpass: {
    input: num("highpass"),
    output: out("highpass-value"),
    render: (v) => (v === 0 ? "выключен" : `${v} Гц`),
  },
  rnnoise: { input: num("rnnoise"), output: null },
  gate: {
    input: num("gate"),
    output: out("gate-value"),
    // 0 дБ порогом не бывает: это «гейт выключен», и сервер получит null.
    render: (v) => (v === 0 ? "выключен" : `${v} дБ`),
  },
  vad: { input: document.getElementById("vad") as HTMLSelectElement, output: null },
  vadThreshold: {
    input: num("vad-threshold"),
    output: out("vad-threshold-value"),
    render: (v) => v.toFixed(2),
  },
  vadRelease: { input: num("vad-release"), output: out("vad-release-value"), render: ms },
  protect: {
    input: num("protect"),
    output: out("protect-value"),
    // 0.5 означает «защита выключена» — так в оригинальном RVC.
    render: (v) => (v >= 0.5 ? "выключена" : v.toFixed(2)),
  },
  f0Threshold: {
    input: num("f0-threshold"),
    output: out("f0-threshold-value"),
    render: (v) => v.toFixed(2),
  },
  jitter: { input: num("jitter"), output: out("jitter-value"), render: ms },
  blockMs: { input: num("block-ms"), output: out("block-ms-value"), render: ms },
  contextMs: { input: num("context-ms"), output: out("context-ms-value"), render: ms },
  crossfadeMs: { input: num("crossfade-ms"), output: out("crossfade-ms-value"), render: ms },
  solaMs: { input: num("sola-ms"), output: out("sola-ms-value"), render: ms },
  thrifty: { input: num("thrifty"), output: out("thrifty-value") },

  // Ниже — то, что живёт на главном экране. Хранится вместе с остальным:
  // разделение на «настройки» и «выбор» пользователю ничего не говорит, а
  // потерянный после перезагрузки микрофон раздражает одинаково.
  pitch: {
    input: num("pitch"),
    output: out("pitch-value"),
    render: (v) => `${v > 0 ? "+" : ""}${v} полутонов`,
  },
  indexRatio: { input: num("index-ratio"), output: out("index-ratio-value") },
  voice: { input: document.getElementById("voice") as HTMLSelectElement, output: null },
  inputDevice: { input: document.getElementById("input-device") as HTMLSelectElement, output: null },
  outputDevice: {
    input: document.getElementById("output-device") as HTMLSelectElement,
    output: null,
  },
};

/** Контролы, которые сервер применяет только на хендшейке. */
const HANDSHAKE_ONLY = [
  // Голос и микрофон читаются один раз, при подключении. Оставленные живыми,
  // они позволяли выбрать другое и ничего не получить — то самое «сделать
  // вид». Выход тут не при чём: он переключается сразу, своим обработчиком.
  "voice",
  "inputDevice",
  "inputGain",
  "highpass",
  "rnnoise",
  "gate",
  "vad",
  "vadThreshold",
  "vadRelease",
  "f0Threshold",
  "jitter",
  "blockMs",
  "contextMs",
  "crossfadeMs",
  "solaMs",
  "thrifty",
] as const;

const value = (knob: Knob) => Number((knob.input as HTMLInputElement).value);
const checked = (knob: Knob) => (knob.input as HTMLInputElement).checked;

export function renderKnob(knob: Knob): void {
  // Подпись рисуется только там, где есть чем: у чекбоксов `render` нет, их
  // подписи ведут syncVad/syncThrifty.
  if (knob.output && knob.render) {
    knob.output.textContent = knob.render(value(knob));
  }
}

export function renderAll(): void {
  for (const knob of Object.values(knobs)) {
    renderKnob(knob);
  }
  syncVad();
  syncThrifty();
}

/**
 * Порог и удержание имеют смысл только при включённом детекторе.
 *
 * У порога вдобавок разные шкалы: у Silero это вероятность речи, у
 * энергетического — среднеквадратичный уровень. Одно и то же число означает в
 * них разное, и 0.5, разумное для вероятности, для уровня недостижимо — гейт
 * не откроется никогда, на выходе тишина. Молча подменять значение при смене
 * режима хуже, чем показать шкалу: тогда видно, что именно настраивается.
 */
export function syncVad(): void {
  const mode = (knobs.vad!.input as HTMLSelectElement).value;
  const off = mode === "off";
  const threshold = knobs.vadThreshold!;

  for (const id of ["vadThreshold", "vadRelease"] as const) {
    (knobs[id]!.input as HTMLInputElement).disabled = off;
  }

  // Режим сменился — порог обязан вернуться к умолчанию нового режима.
  //
  // Перенести число между шкалами значит получить бесшумную поломку: 0.08,
  // разумная вероятность речи для Silero, как среднеквадратичный уровень
  // вчетверо выше умолчания 0.02, и энергетический гейт при ней не открывается
  // почти никогда. На выходе тишина, и ни одной жалобы ни от кого.
  //
  // Первый вызов после загрузки страницы режимом не считается: там
  // восстановлено сохранённое, и подменять его нечем.
  if (lastVadMode !== null && lastVadMode !== mode && !off) {
    const fallback = vadDefaults[mode];
    if (fallback !== undefined) {
      (threshold.input as HTMLInputElement).value = String(fallback);
    }
  }
  lastVadMode = mode;

  if (threshold.output) {
    const scale = mode === "silero" ? "вероятность речи" : "уровень";
    threshold.output.textContent = off
      ? "детектор выключен"
      : `${threshold.render!(value(threshold))} · ${scale}`;
  }
}

/**
 * Экономия канала и шумодав несовместимы, и это не вкус.
 *
 * RNNoise обучен на полном спектре и работает только на 48 кГц: на уже
 * прореженном сигнале чистить ему нечего, а привести 16 кГц обратно к 48
 * сервер, конечно, приведёт — потратив время и не добавив ничего. Поэтому
 * включённый шумодав запрещает экономию, а не молча её обесценивает.
 *
 * В остальных случаях экономия — умолчание: сервер всё равно приводит вход к
 * своим 16 кГц, и 48 означают втрое больший поток без единого выигрыша.
 */
export function syncThrifty(): void {
  const denoising = checked(knobs.rnnoise!);
  const thrifty = knobs.thrifty!;
  const input = thrifty.input as HTMLInputElement;

  // Галочку не трогаем, только запираем — как это сделано с порогом VAD при
  // выключенном детекторе. Снятая, она не вернулась бы обратно после того, как
  // шумодав выключат, и выбор пользователя пропал бы молча. Не применяется она
  // при этом всё равно: см. `thrifty()`.
  input.disabled = denoising;

  if (thrifty.output) {
    thrifty.output.textContent = denoising
      ? "шумодаву нужны 48 кГц"
      : input.checked
        ? "0.5 Мбит/с вверх"
        : "1.5 Мбит/с вверх";
  }
}

/**
 * Silero есть не на каждом сервере.
 *
 * Он общий на весь сервер и задаётся флагом `--silero-vad`. Без него сервер
 * отвергает хендшейк с объяснением — а поскольку Silero стоит умолчанием,
 * первое же подключение к серверу без него провалилось бы. Поэтому режим
 * отключается в списке, а выбор откатывается на энергетический.
 *
 * Возвращает текст для интерфейса, если откат случился.
 */
export function applyCatalog(hasSilero: boolean): string | null {
  const select = knobs.vad!.input as HTMLSelectElement;
  const option = select.querySelector<HTMLOptionElement>('option[value="silero"]');
  if (option) {
    option.disabled = !hasSilero;
    option.textContent = hasSilero ? "Silero" : "Silero (нет модели на сервере)";
  }

  if (!hasSilero && select.value === "silero") {
    // Выключаем, а не подменяем энергетическим. Энергетический — не замена
    // Silero, а другой прибор: он судит по громкости и на тихом микрофоне
    // глушит речь целиком. Молча подсунуть его вместо запрошенного значит
    // сделать вид, что просьба выполнена, и получить тишину без объяснений.
    // Выключенный детектор ничего не режет — пусть шумит, но слышно.
    select.value = "off";
    syncVad();
    // Намеренно не сохраняем: это не выбор пользователя, а вывод из того, что
    // сейчас есть на сервере. Записанный в настройки, он пережил бы появление
    // модели — сервер уже умеет Silero, а клиент всё шлёт запасной вариант,
    // потому что «так сохранено». Решение принимается заново при каждом
    // подключении, по свежему каталогу.
    return (
      "На сервере нет модели Silero — детектор речи выключен. " +
      "Энергетический включайте вручную: он судит по громкости и на тихом " +
      "микрофоне может заглушить речь целиком."
    );
  }

  syncVad();
  return null;
}

/** Заблокировать то, что во время сессии всё равно не применится. */
export function lockHandshakeOnly(locked: boolean): void {
  for (const id of HANDSHAKE_ONLY) {
    knobs[id]!.input.disabled = locked;
  }
  if (!locked) {
    // Списки отпираем, только если есть из чего выбирать: живой пустой список
    // обещает выбор, которого нет.
    //
    // «Голос» на сервере без голосов заперт намеренно (`fillVoices`), и конец
    // сессии не повод его оживлять — там единственный пункт-заглушка с пустым
    // значением. Список микрофонов до первого перечисления пуст вовсе, а
    // «системный по умолчанию» в нём — настоящий выбор, поэтому условия разные.
    unlockIfFilled(knobs.voice!.input as HTMLSelectElement, true);
    unlockIfFilled(knobs.inputDevice!.input as HTMLSelectElement, false);

    syncVad();
    syncThrifty();
  }
}

/**
 * Отпереть список, только если в нём есть что выбирать.
 *
 * @param realValueRequired пункт с пустым значением выбором не считается
 */
function unlockIfFilled(select: HTMLSelectElement, realValueRequired: boolean): void {
  select.disabled = realValueRequired
    ? ![...select.options].some((option) => option.value !== "")
    : select.options.length === 0;
}

/**
 * Сохранённое значение по имени ручки.
 *
 * Нужно спискам, которые наполняются позже своего восстановления: голоса
 * приезжают с каталогом, устройства — после разрешения на микрофон. К моменту
 * `restore()` их `<option>` ещё не существует, и присвоение молча теряется.
 */
export function saved(id: keyof typeof knobs): string | null {
  const raw = storage.read(STORAGE_KEY);
  if (raw === null) {
    return null;
  }
  try {
    const value = (JSON.parse(raw) as Record<string, unknown>)[id];
    return typeof value === "string" ? value : null;
  } catch {
    return null;
  }
}

/**
 * Порог и удержание детектора — его собственные подробности: при включённом
 * детекторе хватает его самого в списке, при выключенном они ни на что не
 * влияют.
 */
const PART_OF_VAD = new Set(["vadThreshold", "vadRelease"]);

/** Отличается ли ручка от того, что стоит в разметке. */
function isChanged(input: HTMLInputElement | HTMLSelectElement): boolean {
  if (input instanceof HTMLSelectElement) {
    const initial = Array.from(input.options).find((o) => o.defaultSelected) ?? input.options[0];
    return initial !== undefined && input.value !== initial.value;
  }
  if (input.type === "checkbox") {
    return input.checked !== input.defaultChecked;
  }
  return input.value !== input.defaultValue;
}

/**
 * Какие ручки внутри `container` сдвинуты с умолчаний — короткими подписями.
 *
 * Нужно свёрнутым разделам: забытый гейт ищут на сервере, потому что в
 * закрытом разделе его не видно. Умолчание — то, что стоит в разметке:
 * заводить для этого отдельные числа значило бы держать две копии.
 */
export function changedIn(container: Element): string[] {
  const names: string[] = [];
  for (const [id, knob] of Object.entries(knobs)) {
    if (PART_OF_VAD.has(id) || !container.contains(knob.input) || !isChanged(knob.input)) {
      continue;
    }
    const label = document.querySelector(`label[for="${knob.input.id}"]`);
    names.push(knob.input.dataset.short ?? label?.textContent?.trim().toLowerCase() ?? id);
  }
  return names;
}

/** То, по чему индикатор ставит метки порогов. */
export function thresholdSettings(): ThresholdSettings {
  const vad = (knobs.vad!.input as HTMLSelectElement).value;
  return {
    inputGainDb: value(knobs.inputGain!),
    gateDb: value(knobs.gate!),
    vad: vad === "energy" || vad === "silero" ? vad : "off",
    vadThreshold: value(knobs.vadThreshold!),
  };
}

/** Сколько миллисекунд держать в буфере приёма. */
export function jitterMs(): number {
  return value(knobs.jitter!);
}

export function thrifty(): boolean {
  // Шумодав старше: при нём экономия недоступна, и полагаться на то, что
  // syncThrifty уже снял галочку, не стоит — восстановление из хранилища
  // может успеть раньше.
  return checked(knobs.thrifty!) && !checked(knobs.rnnoise!);
}

/** Собрать то, что уедет в хендшейке. */
export function toSessionConfig(): SessionConfig {
  const gateDb = value(knobs.gate!);
  const vadMode = (knobs.vad!.input as HTMLSelectElement).value;

  return {
    input_gain_db: value(knobs.inputGain!),
    highpass_hz: value(knobs.highpass!),
    rnnoise: checked(knobs.rnnoise!),
    // Ноль — это «выключен», а не порог: сервер ждёт отсутствия поля.
    gate_threshold_db: gateDb === 0 ? null : gateDb,
    vad_mode: vadMode,
    // Порог отдаём только при включённом детекторе: иначе умолчание берётся
    // у режима, и это правильнее, чем наше число.
    vad_threshold: vadMode === "off" ? null : value(knobs.vadThreshold!),
    vad_release_ms: value(knobs.vadRelease!),
    protect: value(knobs.protect!),
    f0_threshold: value(knobs.f0Threshold!),
    block_ms: value(knobs.blockMs!),
    extra_context_ms: value(knobs.contextMs!),
    crossfade_ms: value(knobs.crossfadeMs!),
    sola_search_ms: value(knobs.solaMs!),
  };
}

/**
 * Подтянуть границы контролов под то, что примет сервер.
 *
 * Числа приезжают в `/defaults`; хардкодить их здесь значило бы предлагать
 * пользователю заведомо отказное, когда сервер обновят.
 */
export function applyLimits(limits: SessionLimits): void {
  if (limits.vad_default_thresholds) {
    vadDefaults = limits.vad_default_thresholds;
  }

  const bound = (id: keyof typeof knobs, max: number) => {
    (knobs[id]!.input as HTMLInputElement).max = String(max);
  };

  bound("inputGain", Math.min(40, limits.max_input_gain_db));
  bound("highpass", Math.min(300, limits.max_highpass_hz));
  bound("vadRelease", Math.min(2000, limits.max_vad_release_ms));
  bound("blockMs", Math.min(200, limits.max_block_ms));
  bound("contextMs", Math.min(2000, limits.max_extra_context_ms));
  bound("crossfadeMs", Math.min(200, limits.max_crossfade_ms));
  bound("solaMs", Math.min(50, limits.max_sola_search_ms));
  (knobs.gate!.input as HTMLInputElement).min = String(
    Math.max(-90, limits.min_gate_threshold_db),
  );
}

/** Сохранить, чтобы подобранное не пропадало при перезагрузке страницы. */
export function save(): void {
  const state: Record<string, string | boolean | number> = { version: DEFAULTS_VERSION };
  for (const [id, knob] of Object.entries(knobs)) {
    const input = knob.input as HTMLInputElement;
    state[id] = input.type === "checkbox" ? input.checked : input.value;
  }
  storage.write(STORAGE_KEY, JSON.stringify(state));
}

export function restore(): void {
  // Настройки раньше жили в localStorage — переносим, чтобы подобранное не
  // пропало при обновлении страницы до этой версии.
  storage.migrate(STORAGE_KEY);

  const raw = storage.read(STORAGE_KEY);
  if (raw === null) {
    return;
  }

  try {
    const state = JSON.parse(raw) as Record<string, string | boolean | number>;

    // Умолчания сменились — сохранённое отбрасываем целиком. Частичное
    // слияние дало бы набор, которого никто не подбирал.
    if (state["version"] !== DEFAULTS_VERSION) {
      storage.remove(STORAGE_KEY);
      return;
    }

    for (const [id, saved] of Object.entries(state)) {
      const knob = knobs[id];
      if (!knob) {
        continue;
      }
      const input = knob.input as HTMLInputElement;
      if (typeof saved === "boolean") {
        input.checked = saved;
      } else if (typeof saved === "string") {
        input.value = saved;
      }
    }
  } catch {
    // Формат мог измениться между версиями. Умолчания уже в разметке, так что
    // просто игнорируем испорченное, а не падаем на старте.
    storage.remove(STORAGE_KEY);
  }
}
